import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../hooks/useSettings';
import logger from '../../utils/logger';

const ChatWidget = () => {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    if (messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content:
            "Hello! 👋 I'm your Evenza AI assistant. I can help you with event marketing, stall fabrication, quotations, and guide you through our website. How can I assist you today?",
          buttons: [
            'Get Quotation',
            'Our Services',
            'Contact Details',
            'How to Use Website',
          ],
        },
      ]);

      // Track chat open - use apiClient for consistent auth
      apiClient
        .post(API_ENDPOINTS.ANALYTICS_TRACK, {
          user_email: user?.email || 'anonymous@example.com',
          page: 'chat_widget',
          action: 'open',
        })
        .catch((error) => {
          if (error.response?.status !== 401) {
            logger.warn('Failed to track chat open', error);
          }
        });
    }
  }, [open, messages.length, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const handleSend = async (messageText = null) => {
    const messageToSend = messageText || input.trim();
    if (!messageToSend || loading) return;

    const userMessage = { role: 'user', content: messageToSend };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setSessionExpired(false);

    try {
      const response = await apiClient.post(API_ENDPOINTS.CHATBOT, {
        message: messageToSend,
        history: messages,
        user_email: user?.email || 'anonymous@example.com',
      });

      if (response.data.success && response.data.response) {
        const responseData = response.data.response;
        let content = '';
        let buttons = [];

        if (typeof responseData === 'string') {
          content = responseData;
          buttons = getDefaultButtons(responseData);
        } else if (typeof responseData === 'object' && responseData !== null) {
          content = responseData.content || responseData.text || responseData.message || JSON.stringify(responseData);
          buttons = responseData.buttons || getDefaultButtons(content);
        } else {
          content = String(responseData);
          buttons = getDefaultButtons(content);
        }

        const newMessage = {
          role: 'assistant',
          content,
          buttons: buttons && buttons.length > 0 ? buttons : getDefaultButtons(content),
        };
        setMessages((prev) => [...prev, newMessage]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'I apologize, but I encountered an issue. Please try again or contact us via WhatsApp/Email for immediate assistance.',
            buttons: ['Contact Details', 'Get Quotation'],
          },
        ]);
      }
    } catch (error) {
      logger.error('Chatbot error:', error);
      
      // Handle 401 (session expired)
      if (error.response?.status === 401) {
        setSessionExpired(true);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Your session has expired. Please log in again to continue chatting.',
            buttons: ['Login', 'Continue as Guest'],
          },
        ]);
      } else {
        // Other errors (network, server, etc)
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'I apologize, but I encountered an error. Please try again or contact us via WhatsApp/Email for immediate assistance.',
            buttons: ['Contact Details', 'Get Quotation'],
          },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleButtonClick = (buttonText) => {
    handleSend(buttonText);
  };

  const getDefaultButtons = (content) => {
    const contentLower = content?.toLowerCase() || '';
    if (contentLower.includes('quotation') || contentLower.includes('price')) {
      return ['Get Detailed Quotation', 'Contact Sales Team', 'View Services'];
    }
    if (contentLower.includes('service')) {
      return ['Stall Fabrication', 'Event Planning', 'Get Quotation'];
    }
    if (contentLower.includes('contact')) {
      return ['WhatsApp', 'Email', 'Call Us'];
    }
    return ['Get Quotation', 'Our Services', 'Contact Details'];
  };

  const renderContactDetails = () => {
    const whatsapp = settings?.whatsapp_number || '7480057900';
    const email = settings?.email || 'evenza@evenza.com';
    const phone = settings?.phone || whatsapp;
    const address = settings?.address || 'AI Events Solution, No 57, Site No 1&2, 5th Cross Road, Veerannapalya, Bengaluru - 560045';

    return (
      <Box sx={{ mt: 1.5, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          📞 Contact Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsAppIcon sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography variant="body2">
              <strong>WhatsApp:</strong>{' '}
              <a href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                {whatsapp}
              </a>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2">
              <strong>Email:</strong>{' '}
              <a href={`mailto:${email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                {email}
              </a>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 18, color: 'info.main' }} />
            <Typography variant="body2">
              <strong>Phone:</strong>{' '}
              <a href={`tel:${phone.replace(/\D/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                {phone}
              </a>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <LocationOnIcon sx={{ fontSize: 18, color: 'error.main', mt: 0.5 }} />
            <Typography variant="body2">
              <strong>Address:</strong> {address}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {/* Floating open button */}
      {!open && (
        <Tooltip title="Chat with Evenza AI Assistant">
          <IconButton
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1300,
              bgcolor: 'primary.main',
              color: 'white',
              width: 56,
              height: 56,
              boxShadow: 4,
              '&:hover': { 
                boxShadow: 6, 
                bgcolor: 'primary.dark',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.3s ease',
            }}
            onClick={() => setOpen(true)}
          >
            <SmartToyIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Chat popup box */}
      {open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: { xs: '90%', sm: 400 },
            maxWidth: '90vw',
            zIndex: 1300,
          }}
        >
          <Paper 
            elevation={8} 
            sx={{ 
              borderRadius: 3, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: '600px',
              maxHeight: '85vh',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 2,
                bgcolor: 'primary.main',
                color: 'white',
                background: 'linear-gradient(135deg, #5c1c73 0%, #E10098 100%)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 36, height: 36 }}>
                  <SmartToyIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Evenza AI Assistant
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                    Online • Ready to help
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Messages */}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                p: 2,
                bgcolor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {messages.map((message, index) => (
                <Box key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: message.role === 'user' ? 'primary.main' : 'secondary.main',
                        width: 32,
                        height: 32,
                        boxShadow: 2,
                      }}
                    >
                      {message.role === 'user' ? (
                        <PersonIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <SmartToyIcon sx={{ fontSize: 18 }} />
                      )}
                    </Avatar>
                    <Box sx={{ maxWidth: '80%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: message.role === 'user' 
                            ? 'primary.main' 
                            : 'white',
                          color: message.role === 'user' ? 'white' : 'text.primary',
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          boxShadow: message.role === 'user' ? 2 : 1,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {message.content}
                      </Box>
                      {message.role === 'assistant' && message.content?.toLowerCase().includes('contact') && renderContactDetails()}
                      {message.role === 'assistant' && message.buttons && message.buttons.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                          {message.buttons.map((button, btnIndex) => (
                            <Chip
                              key={btnIndex}
                              label={button}
                              onClick={() => handleButtonClick(button)}
                              size="small"
                              sx={{
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                },
                                fontSize: '0.75rem',
                                height: '28px',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
              {loading && (
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 28, height: 28 }}>
                    <SmartToyIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box sx={{ p: 1 }}>
                    <CircularProgress size={16} />
                  </Box>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type your message here..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  sx={{ 
                    minWidth: 48, 
                    height: 40,
                    borderRadius: 2,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  <SendIcon fontSize="small" />
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center', fontSize: '0.7rem' }}>
                Powered by Evenza AI • Ask anything about events & marketing
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default ChatWidget;


